import React, { useEffect, useRef, useState } from "react";
import {
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  Radio,
  RadioGroup,
  Stack,
  Button,
  HStack,
  ModalFooter,
  Progress,
  Textarea,
  CheckboxGroup,
  Checkbox,
  Spinner,
  Input,
  useToast,
  Text,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  saveSearchQuery,
  getFieldData,
  saveSearchQueryLocal,
} from "../../actions/search.actions";
import { buildJsonByPath } from "../../utils/format";
import { useNavigate } from "react-router-dom";
import { showAskForLogin } from "../../actions/ui.actions";
import axiosInstance, { axiosInstanceES } from "../../utils/axio-instance";
import { loadGoogleMaps } from "../../utils/googleMaps";
import MatchedVendorsModal from "../../components/homeServices/MatchedVendorsModal";
import { goToMessageChannel } from "../../utils/messageLinks";

function QuestionsWizard(props) {
  const dispatch = useDispatch();
  const uiSelect = useSelector((state) => state.ui);
  const { isOpen, onClose, questions, side, service } = props;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedValue, setSelectedValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  // User's saved addresses (Profile > Addresses), shown before city/state suggestions
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);
  const googleRef = useRef({ autocomplete: null, places: null, sessionToken: null });
  const [selectedLoc, setSelectedLoc] = useState({});
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [isLastQuestion, setIsLastQuestion] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const [createdChannelId, setCreatedChannelId] = useState(null);
  const [showMatchedVendors, setShowMatchedVendors] = useState(false);
  const roleInfo = localStorage.getItem("roles");
  const userId = localStorage.getItem("userid");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const locationQuestion = {
    field_id: 41,
    field_label: "What is the service address or city & state?",
    field_type: "location",
    selectedOption: "",
  };

  useEffect(() => {
    setCurrentQuestionIndex(0);
    setCurrentQuestions([locationQuestion, ...questions]);
  }, [questions]);

  useEffect(() => {
    // Update the selected value when the question changes
    console.log("currentQuestion", currentQuestions[currentQuestionIndex]);
    if (currentQuestions[currentQuestionIndex]) {
      setSelectedValue(currentQuestions[currentQuestionIndex].selectedOption);
    }
  }, [currentQuestionIndex]);

  // Top saved-address matches for what's typed (most recent when empty).
  // Searched server-side since agents can have many; the counter drops
  // responses that arrive after a newer keystroke's.
  const savedAddressReq = useRef(0);
  const fetchSavedAddresses = (term) => {
    if (!userId) return;
    const reqNo = ++savedAddressReq.current;
    axiosInstance
      .get("/addresses", { params: { page: 1, size: 5, term: term.trim() || undefined } })
      .then((resp) => {
        if (reqNo === savedAddressReq.current) setSavedAddresses(resp.data?.data || []);
      })
      .catch((err) => console.log("Failed to load saved addresses", err));
  };

  useEffect(() => {
    if (!isOpen) return;
    loadGoogleMaps()
      .then((maps) => {
        googleRef.current.autocomplete = new maps.places.AutocompleteService();
        googleRef.current.places = new maps.places.PlacesService(document.createElement("div"));
      })
      .catch((err) => console.log("Google Maps failed to load", err));
  }, [isOpen]);

  const getSessionToken = () => {
    if (!googleRef.current.sessionToken && window.google?.maps?.places) {
      googleRef.current.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    }
    return googleRef.current.sessionToken;
  };

  const fetchAddressSuggestions = (term) => {
    const { autocomplete } = googleRef.current;
    if (!autocomplete) return;
    autocomplete.getPlacePredictions(
      {
        input: term,
        types: ["address"],
        componentRestrictions: { country: "us" },
        sessionToken: getSessionToken(),
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setAddressSuggestions(predictions.slice(0, 5));
        } else {
          setAddressSuggestions([]);
        }
      }
    );
  };

  const onLocKeyPress = (e) => {
    const term = e.target.value;
    fetchSavedAddresses(term);
    if(term.length<=2){
      setSuggestions([]);
      setAddressSuggestions([]);
    }
    if(term.length>2){
      fetchAddressSuggestions(term);
      axiosInstanceES
        .get(`/search/locationSuggestion?term=${term}`)
        .then((resp) => {
          const suggData = resp.data;
          if(suggData && suggData.length>0){
            setSuggestions(suggData.slice(0, 10));
          }else{
            setSuggestions([]);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
    e.preventDefault();
  };

  const handleSuggestionClick = (sugg) => {
    setSuggestions([]);
    setAddressSuggestions([]);
    setShowSavedAddresses(false);
    setSelectedValue(sugg.city + ", " + sugg.stateCode);
    currentQuestions[currentQuestionIndex].selectedOption = sugg.city + ", " + sugg.stateCode;
    // City/state selection: no placeId
    setSelectedLoc(sugg);

  };

  const handleSavedAddressClick = (saved) => {
    setSuggestions([]);
    setAddressSuggestions([]);
    setShowSavedAddresses(false);
    setSelectedValue(saved.fullAddress);
    currentQuestions[currentQuestionIndex].selectedOption = saved.fullAddress;
    // Same shape as a Google Places pick; the api re-saves it by placeId and
    // links the request to the saved address.
    setSelectedLoc({
      city: saved.city,
      stateCode: saved.state,
      stateName: saved.fullState,
      location: saved.location || undefined,
      fullAddress: saved.fullAddress,
      street: saved.street,
      zip: saved.zip,
      placeId: saved.placeId,
    });
  };

  const handleAddressClick = (prediction) => {
    const { places } = googleRef.current;
    setSuggestions([]);
    setAddressSuggestions([]);
    setShowSavedAddresses(false);
    if (!places) return;
    places.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["place_id", "formatted_address", "address_components", "geometry"],
        sessionToken: getSessionToken(),
      },
      (place, status) => {
        // Session ends once details are fetched; next search starts a new one
        googleRef.current.sessionToken = null;
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          toast({
            title: "Unable to fetch address details, please try again",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          return;
        }
        const components = place.address_components || [];
        const find = (...types) =>
          components.find((c) => types.some((t) => c.types.includes(t)));
        const streetNumber = find("street_number")?.long_name || "";
        const route = find("route")?.long_name || "";
        const city = find("locality", "sublocality", "postal_town", "administrative_area_level_3");
        const state = find("administrative_area_level_1");
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();
        const fullAddress = place.formatted_address || prediction.description;

        setSelectedValue(fullAddress);
        currentQuestions[currentQuestionIndex].selectedOption = fullAddress;
        setSelectedLoc({
          city: city?.long_name || "",
          stateCode: state?.short_name || "",
          stateName: state?.long_name || "",
          location: lat !== undefined && lng !== undefined ? { lat, lon: lng } : undefined,
          fullAddress,
          street: [streetNumber, route].filter(Boolean).join(" "),
          zip: find("postal_code")?.long_name || "",
          placeId: place.place_id,
        });
      }
    );
  };

  const handleOptionChange = (value) => {
    const updatedQuestions = [...currentQuestions];
    updatedQuestions[currentQuestionIndex].selectedOption = value;
    setSelectedValue(value);
    setCurrentQuestions([...updatedQuestions]);
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  useEffect(()=>{
    setIsLastQuestion(currentQuestionIndex === currentQuestions.length - 1);
  },[currentQuestionIndex])

  const nextQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedValue("");
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    // Handle form submission here
    const fieldids = [];
    const qnAns = currentQuestions.map((question) => {
      fieldids.push(question.field_id);
      return {
        questionId: question.id,
        fieldLabel: question.field_label,
        fieldId: question.field_id,
        fieldValue: question.selectedOption,
      };
    });
    fieldids.push(service.field_id);
    const fieldData = await getFieldData(fieldids);
    
    const filterData = fieldData.map((field) => {
      // below is the correct way to get the answer
      const qnAnsData = qnAns.find((qn) => parseInt(qn.fieldId) === parseInt(field.field_id));
      if(!qnAnsData){
        console.log("No answer found for field", field.field_id);
        return null;
      }else{
        console.log("qnAnsData for field", field.field_id, qnAnsData);
      }
      let fieldValue = qnAnsData.fieldValue;
      if(field.field_type === "float"){
        //fieldValue = parseFloat(fieldValue);
      } else if(field.field_type === "boolean"){
        fieldValue = (fieldValue == "yes" || fieldValue == "true")?"true":false;
      }
      return {
        [field.field_name]: fieldValue,
      };
    });
    const serviceField = fieldData.filter((field) => parseInt(field.field_id) === parseInt(service.field_id))[0];
    console.log("serviceField, service", serviceField, service);
    filterData.push({[serviceField.field_name]: service.field_value});

    let searchQueryJson = buildJsonByPath(filterData);
    console.log("searchQueryJson after building...", searchQueryJson);
    searchQueryJson.main = {
      propertyStatus: "active",
      recordCreatedDate: new Date().toISOString(),
      recordUpdatedDate: new Date().toISOString(),
      createdRole: roleInfo,
      createdByUser: userId,
      members: (userId)?[userId]:[],
      recordType: "request",
      side,
    };
    searchQueryJson.location = selectedLoc.location;
    searchQueryJson.data = {...searchQueryJson.data, 
      propertyInfo:{
        ...searchQueryJson.data?.propertyInfo,
        address: {
          city: selectedLoc.city,
          fullState: selectedLoc.stateName,
          state: selectedLoc.stateCode,
          // Only full-address selections (Google Places) carry a placeId
          ...(selectedLoc.placeId && {
            fullAddress: selectedLoc.fullAddress,
            street: selectedLoc.street,
            zip: selectedLoc.zip,
            placeId: selectedLoc.placeId,
          }),
        }
      }
    };
      console.log("searchQueryJson before saving...", searchQueryJson);
    if (userId) {
      try{
        const resp = await saveSearchQuery(searchQueryJson);
        console.log("response", resp.status)
        if(resp.status === 200){
         toast({
            title: 'Service request saved successfully',
            status: 'success',
            duration: 3000,
            isClosable: true,
              position: 'top-right',
          });
          onClose();
          // Show matched vendors (sorted by rating, verified first) instead
          // of redirecting to /search — the modal keeps a "View all" link.
          const requestId = resp.data?.requestResp?.id;
          // Message channel created with the request (null if messaging is down).
          const channelId = resp.data?.channel?.channelId || null;
          if (requestId) {
            setCreatedRequestId(requestId);
            setCreatedChannelId(channelId);
            setShowMatchedVendors(true);
          } else if (channelId) {
            goToMessageChannel(channelId);
          } else {
            setTimeout(() => {
              navigate("/search/?type=request&page=1");
            }, 2000);
          }
          setIsLoading(false);
        }else{
            throw new Error("Failed to save service request");
        }
      }catch(e){
        toast({
          title: 'Error',
          description: e.message || 'Failed to save service request. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }finally{
        setCurrentQuestionIndex(0);
        setIsLoading(false);
      }
      // redirect to search page with type=request
    } else {
      saveSearchQueryLocal(searchQueryJson);
      dispatch(showAskForLogin(true));
    }
  };

  const handleClose = () => {
    setCurrentQuestionIndex(0);
    onClose();
    setIsLoading(false);
  };
  const matchedSavedAddresses = showSavedAddresses ? savedAddresses : [];

  // Location must be picked from the suggestions, not free text
  const isLocationUnselected =
    currentQuestions[currentQuestionIndex]?.field_type === "location" && !selectedLoc.city;

  if(currentQuestions.length === 0){
    return <>loading</>;
  }
  return (
    <>
    <MatchedVendorsModal
      isOpen={showMatchedVendors}
      onClose={() => setShowMatchedVendors(false)}
      requestId={createdRequestId}
          channelId={createdChannelId}
    />
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "sm", md: "xl", lg: "2xl", xl: "2xl" }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader className="sticky top-0 bg-white z-10 shadow-md">
          {currentQuestions.length>0? <Progress
            rounded={"lg"}
            size="sm"
            value={((currentQuestionIndex + 1) / currentQuestions.length) * 100}
          />: null}
        </ModalHeader>
        <ModalCloseButton
          onClick={handleClose}
          _hover={{ backgroundColor: "gray.200", borderRadius: "lg" }}
          backgroundColor="white"
          zIndex={50}
        />
        <ModalBody p={{ base: 4, md: 10 }}>
          {uiSelect.isLoading && (
            <div className="col-span-4 text-center">
              <Spinner size="xl" />
            </div>
          )}
          <VStack spacing={6} align="stretch">
            <Text fontSize={{ base: "sm" }} textAlign={"center"}>{service.service_name}</Text>
            <FormControl>
              <FormLabel fontSize={{ base: "lg", md: "xl" }} fontWeight={"bold"} textAlign={"center"}>
                {currentQuestions[currentQuestionIndex].field_label}
              </FormLabel>
              {(() => {
                switch (currentQuestions[currentQuestionIndex].field_type) {
                  case "select":
                    return (
                      <RadioGroup
                        onChange={handleOptionChange}
                        value={currentQuestions[currentQuestionIndex].selectedOption}
                        pt={{ base: 4, md: 10 }}
                      >
                        <Stack spacing={3}>
                          {currentQuestions[currentQuestionIndex].options?.map((option, index) => (
                            <div
                              key={option.id}
                              className="border border-gray-300 rounded-lg p-3"
                            >
                              <Radio
                                key={option.id}
                                value={option.option_value}
                                size="lg"
                              >
                                {option.option_label}
                              </Radio>
                            </div>
                          ))}
                        </Stack>
                      </RadioGroup>
                    );
                  case "multi-select":
                    return (
                      <CheckboxGroup
                        onChange={handleOptionChange}
                        value={currentQuestions[currentQuestionIndex].selectedOption}
                        pt={{ base: 4, md: 10 }}
                      >
                        <Stack spacing={3}>
                          {currentQuestions[currentQuestionIndex].options?.map((option, index) => (
                            <div
                              key={option.id}
                              className="border border-gray-300 rounded-lg p-3"
                            >
                              <Checkbox
                                key={option.id}
                                value={option.option_value}
                                size="lg"
                              >
                                {option.option_label}
                              </Checkbox>
                            </div>  
                          ))}
                        </Stack>
                      </CheckboxGroup>
                    );
                  case "text":
                    return (
                      <Textarea
                        maxH="3lh"
                        className="w-full border border-gray-300 rounded-md p-2 my-4"
                        value={currentQuestions[currentQuestionIndex].selectedOption}
                        onChange={(e) => handleOptionChange(e.target.value)}
                        placeholder={"Details matter. It's worth providing better details to get it right."}
                      />
                    );
                  case "input":
                    return (
                      <Input
                        value={currentQuestions[currentQuestionIndex].selectedOption}
                        onChange={(e) => handleOptionChange(e.target.value)}
                        placeholder={currentQuestions[currentQuestionIndex].field_label}
                        mt={4}
                        mb={4}
                      />
                    );
                  case "price":
                    return (
                      <Input
                        value={currentQuestions[currentQuestionIndex].selectedOption ? 
                          `$${currentQuestions[currentQuestionIndex].selectedOption}` : 
                          ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          handleOptionChange(value);
                        }}
                        placeholder={currentQuestions[currentQuestionIndex].field_label}
                        mt={4}
                        mb={4}
                      />
                    );
                  case "location":
                    return (
                      <div className="flex flex-col gap-2">
                        <Input
                          id="requestLocation"
                          type="text"
                          autoCorrect="off"
                          autoComplete="off"
                          value={currentQuestions[currentQuestionIndex].selectedOption}
                          onChange={(e) => {
                            // Typing invalidates the previous selection
                            setSelectedLoc({});
                            handleOptionChange(e.target.value);
                          }}
                          onKeyUp={(e) => onLocKeyPress(e)}
                          onFocus={(e) => {
                            setShowSavedAddresses(true);
                            fetchSavedAddresses(e.target.value);
                          }}
                          className="text-gray-500 text-sm px-3 py-3 rounded-lg bg-white border border-gray-200"
                          placeholder="Enter the full address, or city and state"
                        />
                        <div className="absolute w-full z-10">
                          {(matchedSavedAddresses.length > 0 || suggestions.length > 0 || addressSuggestions.length > 0) && (
                            <ul className="absolute top-10 left-0 right-0 bg-white border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                              {matchedSavedAddresses.length > 0 && (
                                <li className="px-2 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase">
                                  Your Addresses
                                </li>
                              )}
                              {matchedSavedAddresses.map((saved) => (
                                <li
                                  key={saved.addressId}
                                  className="p-2 cursor-pointer hover:bg-gray-100 text-sm"
                                  onClick={() => handleSavedAddressClick(saved)}
                                >
                                  {saved.fullAddress}
                                </li>
                              ))}
                              {suggestions.length > 0 && (
                                <li className={`px-2 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase${matchedSavedAddresses.length > 0 ? " border-t border-gray-100" : ""}`}>
                                  City, State
                                </li>
                              )}
                              {suggestions.map((suggestion, index) => (
                                <li
                                  key={index}
                                  className="p-2 cursor-pointer hover:bg-gray-100 text-sm"
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                >
                                  {suggestion.city +
                                    ", " +
                                    suggestion.stateCode}
                                </li>
                              ))}
                              {addressSuggestions.length > 0 && (
                                <li className="px-2 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase border-t border-gray-100">
                                  Address
                                </li>
                              )}
                              {addressSuggestions.map((prediction) => (
                                <li
                                  key={prediction.place_id}
                                  className="p-2 cursor-pointer hover:bg-gray-100 text-sm"
                                  onClick={() => handleAddressClick(prediction)}
                                >
                                  {prediction.description}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })()}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter p={{ base: 4, md: 6 }}>
          <HStack 
            align="stretch" 
            justify="space-between" 
            w="full"
            spacing={{ base: 2, md: 4 }}
          >
            <Button
              onClick={prevQuestion}
              isDisabled={currentQuestionIndex === 0}
              variant="outline"
              size={{ base: "sm", md: "md" }}
            >
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isDisabled={!selectedValue || isLocationUnselected}
                isLoading={isLoading}
                variant="outline"
                size={{ base: "sm", md: "md" }}
              >
                Submit
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                onClick={nextQuestion}
                isDisabled={!selectedValue || isLocationUnselected}
                variant="outline"
                size={{ base: "sm", md: "md" }}
              >
                Next
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
}

export default QuestionsWizard;
